import { useMemo } from 'react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import ImagePoint001 from '@/assets/images/guide/img_point_001_upload.svg';
import ImagePoint002 from '@/assets/images/guide/img_point_002.svg';
import ImagePoint003 from '@/assets/images/guide/img_point_003.svg';
import ImagePoint004 from '@/assets/images/guide/img_point_004.svg';
import Button from '@/components/elements/Button/BaseButton';
import Image from '@/components/elements/Image';
import { RACE_TYPE_PARAM_KEY } from '@/config/constants/query';
import { useUserContext } from '@/contexts/user';
import { RaceType } from '@/modelTypes';
import { GuidePageTitles } from '@/pages/Guide/types';
import { GuidePaths } from '@/routes/Guide';

import {
  Box,
  BoxImage,
  BoxImageNew,
  ButtonContainer,
  Center,
  Container,
  ContentBox,
  LinkButtonSmall,
  Text,
  TextLarge,
  TextLink,
  TextNote,
  Title,
  TitleBox,
  TitleSecondary,
  TitleSmall,
} from '../style';
import { Point, TypeMulti, TypeRace, TypeWrapper } from './style';

const GuideAboutTemplate: React.FC = () => {
  const { isGuest, isLoggedIn } = useUserContext();

  const isShowSignup = useMemo(
    () => isGuest || !isLoggedIn,
    [isGuest, isLoggedIn],
  );

  return (
    <Container>
      <ContentBox>
        <Title>{GuidePageTitles.About}</Title>
        <TextLarge>
          DMM競輪は、スマートフォンやパソコンでいつでもどこでもカンタンに競輪投票やチャリロト・Dokanto!の購入ができるサービスです。
        </TextLarge>
        <TextNote>
          <i>※</i>
          「DMM競輪」は20歳からお楽しみいただけます。
        </TextNote>
        <TypeWrapper>
          <TypeRace>
            <TitleSmall>競輪投票</TitleSmall>
            <Text>
              スタンダードな投票形式。出走表・オッズ・選手情報等を参考に、着順を予想して投票します。
            </Text>
            <ButtonContainer>
              <LinkButtonSmall
                to={`/guide/${GuidePaths.Voting}?${RACE_TYPE_PARAM_KEY}=${RaceType.Singlerace}`}
              >
                投票方法
              </LinkButtonSmall>
            </ButtonContainer>
          </TypeRace>
          <TypeMulti>
            <TitleSmall>チャリロト・Dokanto!</TitleSmall>
            <Text>
              複数レースの1着を選ぶ重勝式車券。コンピュータが自動的に買い目を選んでくれるクイックピック方式なら競輪の知識がなくてもOK。
            </Text>
            <ButtonContainer>
              <LinkButtonSmall
                to={`/guide/${GuidePaths.Voting}?${RACE_TYPE_PARAM_KEY}=${RaceType.Multirace}`}
              >
                購入方法
              </LinkButtonSmall>
            </ButtonContainer>
          </TypeMulti>
        </TypeWrapper>
      </ContentBox>
      <ContentBox>
        <TitleSecondary>DMM競輪の魅力</TitleSecondary>
        <Point>
          <Box>
            <BoxImage>
              <Image srcSet={ImagePoint001} alt="" width="80px" height="80px" />
            </BoxImage>
            <div>
              <TitleBox>最短即日で利用開始</TitleBox>
              <Text>
                DMM競輪のご利用には公的書類のアップロードによる本人確認が必要となります。
                <br />
                公的書類の確認は最短即日で完了します。
              </Text>
            </div>
          </Box>
          {isShowSignup && (
            <ButtonContainer>
              <Link to="/signup">
                <Button
                  type="button"
                  bgColor="primary"
                  size="m"
                  radius="l"
                  height="56px"
                  fontWeight="bold"
                >
                  今すぐ利用登録する（無料）
                </Button>
              </Link>
            </ButtonContainer>
          )}
        </Point>
        <Point>
          <Box>
            <BoxImageNew>
              <Image srcSet={ImagePoint002} alt="" width="80px" height="80px" />
            </BoxImageNew>
            <div>
              <TitleBox>「投票成績」であなたの投票履歴を分析！</TitleBox>
              <Text>
                「投票成績」では、月ごとの投票額・払戻額・回収率などの成績を参照することができます。
                <br />
                カレンダー表示だから、ひと目でその月の投票状況が分かります。
                <br />
                さらに、月間・年間・通算の収支推移をグラフで表示する「収支分析」機能も追加しました。
                <br />
                投票すればするほどデータが蓄積され、競輪場別、賭式別など様々な視点で成績確認ができるので自己分析に活用することができます。
              </Text>
            </div>
          </Box>
          <ButtonContainer>
            <Link to={isShowSignup ? '' : '/bets/analyze'}>
              <Button
                type="button"
                outline
                bgColor="primary"
                size="m"
                radius="l"
                height="56px"
                fontWeight="bold"
                disabled={isShowSignup}
              >
                投票成績を見る
              </Button>
            </Link>
          </ButtonContainer>
          {isShowSignup && (
            <Center>
              <TextNote>
                <i>※</i>
                投票成績はDMM競輪会員のみご利用いただけます。
              </TextNote>
              <Text>
                <TextLink to="/signup">
                  DMM競輪利用登録（無料）はこちら
                </TextLink>
              </Text>
            </Center>
          )}
        </Point>
        <Point>
          <Box>
            <BoxImageNew>
              <Image srcSet={ImagePoint003} alt="" width="80px" height="80px" />
            </BoxImageNew>
            <div>
              <TitleBox>「選手メモ」機能であなただけのデータを蓄積！</TitleBox>
              <Text>
                選手一人ひとりに対してメモを追加できる「選手メモ」機能を追加しました。
                <br />
                レースごとにメモを追加することができるので、その日レースの記録や選手の特徴など、自分だけのメモとして記録していくことが可能です。
                <br />
                追加したメモは投票ページ等でいつでも参照できるので投票に役立てることができます。
              </Text>
            </div>
          </Box>
        </Point>
        <Point>
          <Box>
            <BoxImage>
              <Image srcSet={ImagePoint004} alt="" width="80px" height="80px" />
            </BoxImage>
            <div>
              <TitleBox>幅広い決済手段に対応</TitleBox>
              <Text>
                DMM競輪では、専用電子マネー「toreta+（トレタプラス）」を使って投票を行います。
                <br />
                様々な決済手段をご用意しておりますので、いつでも簡単にチャージ＆投票が可能です。
              </Text>
            </div>
          </Box>
          <ButtonContainer>
            <LinkButtonSmall to={`/guide/${GuidePaths.Toreta}`}>
              toreta+ のチャージ方法を確認する
            </LinkButtonSmall>
          </ButtonContainer>
        </Point>
      </ContentBox>
    </Container>
  );
};

export default GuideAboutTemplate;
